import {
  CellContext,
  Column,
  ColumnDef,
  ColumnFiltersState,
  ColumnPinningState,
  ColumnResizeMode,
  ExpandedState,
  OnChangeFn,
  PaginationState,
  Row,
  RowModel,
  RowSelectionState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import classNames from "classnames";
import React from "react";
import { Draggable, DropResult, DragUpdate } from "react-beautiful-dnd";
import useLoadingPresentation, { LoadingPresentation } from "../../../shared/hooks/useLoadingPresentation";
import KuikaButton from "../kuika-button/kuika-button";
import KuikaCheckbox from "../kuika-checkbox/kuika-checkbox";
import KuikaDropdownMenu from "../kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEmptyState from "../kuika-empty-state/kuika-empty-state";
import KuikaInput from "../kuika-input/kuika-input";
import { PointerAimedDragDropContext, PointerAimedDroppable } from "../kuika-pointer-dnd/pointer-aimed-dnd";
import KuikaPopover from "../kuika-popover/kuika-popover";
import KuikaSkeleton from "../kuika-skeleton/kuika-skeleton";
import KuikaSpinnerSmall from "../kuika-spinner-small/kuika-spinner-small";
import KuikaSvg from "../kuika-svg/kuika-svg";
import KuikaSmallBoldText from "../typography/SmallBoldText/SmallBoldText";
import KuikaTextMedium from "../typography/TextMedium/TextMedium";
import "./kuika-table.scss";

/** Which shape the table's empty band takes: a KuikaEmptyState variant, or no band at all. */
export type KuikaTableEmptyVariant = "table" | "page" | "none";

/** Filter interaction style for a column header button. */
export type KuikaTableFilterVariant = "text" | "multiselect";

export interface KuikaTableColumn<T> {
  id: string;
  header: () => React.ReactNode;
  cell: (row: T, tableRow: Row<T>) => React.ReactNode;
  /** A pixel count, or a CSS length such as `"22%"` for a column that takes a share of the
   *  table rather than a fixed size. A resized column always keeps the size it was dragged to. */
  width?: number | string;
  /** With `horizontalScroll`, the narrowest a column without a pixel `width` gets, in pixels — for a
   *  column sized by what it holds rather than by the shared `MIN_UNSIZED_COLUMN_WIDTH`. */
  minWidth?: number;
  accessorKey?: keyof T & string;
  accessorFn?: (row: T) => unknown;
  enableSorting?: boolean;
  /** Which way the first click sorts. tanstack descends first on numbers and dates. */
  sortDescFirst?: boolean;
  enableColumnFilter?: boolean;
  enableResizing?: boolean;
  align?: "left" | "center" | "right";
  noWrap?: boolean;
  cellClassName?: (row: T) => string;
  /** How the column's header filter popover behaves. `multiselect` shows a
   *  searchable checkbox list of unique values; `text` shows a single input. */
  filterVariant?: KuikaTableFilterVariant;
  /** Optional formatter for labels shown in the multiselect filter list. */
  filterValueLabel?: (value: unknown) => string;
  /** Optional `data-testid` set on the column's header `<th>` — the element that
   *  also carries `aria-sort`. Lets E2E tests target a header by a stable id
   *  instead of its visible label or internal class names. */
  headerTestId?: string;
}

/** Custom fields stored on each TanStack `columnDef.meta`. Typed so header/cell
 *  render code can read them without an `any` cast. */
interface KuikaTableColumnMeta<T> {
  /** A width tanstack cannot hold: its own sizing is numeric, so a CSS length travels here. */
  widthValue?: string;
  minWidth?: number;
  align?: "left" | "center" | "right";
  noWrap?: boolean;
  cellClassName?: (row: T) => string;
  filterVariant?: KuikaTableFilterVariant;
  filterValueLabel?: (value: unknown) => string;
  headerTestId?: string;
}

export interface KuikaTableDragDrop<T> {
  /** Droppable list a row belongs to. Consecutive equal ids form one group. */
  getGroupId: (row: T) => string;
  getDraggableId: (row: T) => string;
  /** Rows that cannot be picked up (a grouping header, a read-only row). */
  isRowDraggable?: (row: T) => boolean;
  /**
   * What the handle announces. Say what the drag DOES on this surface — "reorder" is
   * wrong for a table whose groups are folders, and the default cannot know which it
   * is. Keyboard instructions are rbd's own; don't repeat them here.
   */
  handleLabel?: string;
  onDragEnd: (result: DropResult) => void;
  onDragUpdate?: (update: DragUpdate) => void;
}

/**
 * Turns the flat `data` array into a TREE. The table then owns the flattening, the
 * per-row depth and the indentation that callers used to hand-roll.
 */
export interface KuikaTableTree<T> {
  /**
   * A row's children, or undefined for a leaf. Runs for every row AND every sub-row,
   * so hand it an already-built tree rather than computing children per call.
   *
   * When `dragDrop` is also on, a node's own leaf items MUST come BEFORE its child
   * folders: rows sharing a drag group have to stay contiguous, and a child folder
   * emitted in the middle would reopen the parent's group further down.
   */
  getSubRows: (row: T, index: number) => T[] | undefined;
  /**
   * Whether a row shows its children. Owning this is what lets a caller keep a
   * DEFAULT-OPEN tree — store the collapsed exceptions and answer from those. Seeding
   * tanstack's own expanded record instead would silently collapse every row added
   * after the seed.
   */
  isRowExpanded: (row: T) => boolean;
  /** Id of the column whose cell carries the depth indent. */
  treeColumnId: string;
}

/** How a KuikaTable shows `loading` — see `KuikaTableProps.loadingVariant`. */
export type KuikaTableLoadingVariant = "spinner" | "skeleton";

export interface KuikaTableProps<T> {
  data: T[];
  columns: KuikaTableColumn<T>[];
  rowKey: (row: T) => string;
  /**
   * Optional `data-testid` for each row's `<tr>`. An entity list is addressed BY ENTITY, and a
   * row's own name cell is middle-truncated into head/tail spans, so its text is not an identity
   * — a caller that wants addressable rows supplies the id it derives from the entity itself.
   */
  rowTestId?: (row: T) => string;
  pageSize?: number;
  showCountLabel?: string;
  enableSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /**
   * The caller sorts, not the table: headers stay clickable and keep reporting their state,
   * but `data` renders in the order it arrives. Needed when a comparator cannot express the
   * order — a tree whose root mixes kinds, where one of them must stay on top either way.
   */
  manualSorting?: boolean;
  enableColumnFilters?: boolean;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  enableGlobalFilter?: boolean;
  globalFilter?: string;
  onGlobalFilterChange?: OnChangeFn<string>;
  enableColumnVisibility?: boolean;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  enableColumnResizing?: boolean;
  columnResizeMode?: ColumnResizeMode;
  enablePinning?: boolean;
  columnPinning?: ColumnPinningState;
  onColumnPinningChange?: OnChangeFn<ColumnPinningState>;
  enableMultiSort?: boolean;
  enablePagination?: boolean;
  manualPagination?: boolean;
  /** When false, keep the current page across data/filter/sort changes (clamping if
   *  the data shrinks) instead of resetting to page 1. Unset = tanstack default. */
  autoResetPageIndex?: boolean;
  /** Turns to the page holding this row. Set it after an edit that can reorder the rows —
   *  a rename moves a row under a name sort — so the reader ends up where the row went. */
  revealRowKey?: string | null;
  /** Fired when that row is on the page shown, so the caller can drop the key again. */
  onRowRevealed?: () => void;
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  pageCount?: number;
  totalRows?: number;
  initialState?: any;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  /**
   * `skeleton` (default): skeleton rows while there are no rows yet — drawn only after a short
   * delay, so a quick load never flashes them — and, while rows are on screen, the rows stay,
   * dimmed under the spinner. `spinner`: the spinner in every case.
   */
  loadingVariant?: KuikaTableLoadingVariant;
  /** Skeleton rows to draw. Default: the page size, at most five. */
  skeletonRowCount?: number;
  emptyIcon?: React.ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  /**
   * Which KuikaEmptyState shape the empty band wears. `table` (the default) is the flat, untiled
   * one a grid has always had. `page` is the large page-level state — the one the project list
   * and the tabbed landing screens share, so a landing's empty tab reads like every other.
   * `none` draws no band at all: the heading stands alone and the host says what it wants under
   * it — for a grid whose frame has less room than the primitive's 240px floor (a run drawer).
   */
  emptyVariant?: KuikaTableEmptyVariant;
  showHeader?: boolean;
  enableExpanding?: boolean;
  renderSubComponent?: (row: T) => React.ReactNode;
  getRowCanExpand?: (row: T) => boolean;
  expanded?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
  /** Per-row class hook. `tableRow` carries `depth` for tree rows. */
  rowClassName?: (row: T, tableRow: Row<T>) => string | undefined;
  /** Renders `data` as a tree of expandable parent rows. See `KuikaTableTree`. */
  tree?: KuikaTableTree<T>;
  /**
   * Makes rows draggable between groups. Rows that share a `getGroupId` MUST be
   * contiguous in `data` — each run becomes its own `<tbody>`, which is what gives
   * every group a real drop target of its own (a table may hold many tbodies).
   */
  dragDrop?: KuikaTableDragDrop<T>;
  layoutMode?: "fixed" | "auto";
  sortIndicatorVariant?: "hover" | "inline";
  /**
   * Drop the container's 400px floor so the frame ends at the last row. Opt-in:
   * the default keeps a stable frame for tables that page/filter down to 1-2 rows,
   * but a short list then renders a tall band of empty white. Loading (300px) and
   * the empty state (240px) keep their own minimums either way.
   */
  fitContentHeight?: boolean;
  /**
   * `card` (default) draws the table as a framed card: one hairline around rows and footer
   * together, on the 40px+ rung. `flush` drops the outer frame for a table that fills a surface
   * edge to edge (a drawer body, a panel), where that surface's own edges close it.
   */
  frame?: "card" | "flush";
  /**
   * Columns that do not fit scroll sideways inside the frame. Without it the trailing ones are cut
   * off by the frame: `table-layout: fixed` makes the table as wide as its declared widths, and a
   * column that declares none gets whatever is left — nothing, in a narrow frame. On, the table is
   * floored at its pixel widths plus, for every other column, its `minWidth` or else
   * `MIN_UNSIZED_COLUMN_WIDTH`.
   *
   * The scroller is a scroll container only while the columns actually overflow it (measured), so
   * a frame they fit changes nothing. While they overflow it is the rows' nearest scroll container:
   * a sticky heading row pins to it, and react-beautiful-dnd takes it for the droppables' scroll
   * parent, so the page's auto-scroll during a drag may degrade. Opt-in for that reason — a frame
   * that scrolls its own rows vertically (a sticky heading row) keeps it off. Where a drop lands is
   * not affected: drops are aimed with the pointer (`kuika-pointer-dnd`), not by rbd's geometry.
   */
  horizontalScroll?: boolean;
  "data-testid"?: string;
}

/**
 * How long a revealed row stays tinted. The single source: the row carries it to the stylesheet as
 * `--kuika-table-reveal-duration`, and the class is dropped once it has run.
 */
const REVEAL_HIGHLIGHT_MS: number = 1400;

/**
 * Skeleton rows on a first load, when there is no page to count. Five table rows and the header
 * stand about as tall as the empty state, so an empty answer lands without a jump.
 */
const FIRST_LOAD_SKELETON_ROWS: number = 5;
/**
 * With `horizontalScroll`, the narrowest a column without a pixel width gets — a share of the table
 * (`"22%"`) or no width at all. Enough for a name and its icon to stay readable.
 */
const MIN_UNSIZED_COLUMN_WIDTH: number = 160;
/** The drag handle's injected column — keep in step with `.kuika-table__drag-col`. */
const DRAG_COLUMN_WIDTH: number = 24;

const SKELETON_SQUARE_SIZE: string = "calc(var(--row-h-table) / 2)";
const SKELETON_BAR_HEIGHT: string = "var(--gap-m)";

interface IKuikaTableRowStyle extends React.CSSProperties {
  "--kuika-tree-depth"?: number;
  "--kuika-table-reveal-duration"?: string;
}

const KuikaTable = <T extends object>({
  data,
  columns,
  rowKey,
  rowTestId,
  pageSize = 10,
  showCountLabel,
  enableSorting = false,
  sorting: controlledSorting,
  onSortingChange,
  manualSorting = false,
  enableColumnFilters = false,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange,
  enableGlobalFilter = false,
  globalFilter: controlledGlobalFilter,
  onGlobalFilterChange,
  enableColumnVisibility = false,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange,
  enableRowSelection = false,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  enableColumnResizing = false,
  onRowClick,
  columnResizeMode = "onChange",
  enablePinning = false,
  columnPinning: controlledColumnPinning,
  onColumnPinningChange,
  enableMultiSort = true,
  enablePagination = true,
  manualPagination = false,
  autoResetPageIndex,
  revealRowKey,
  onRowRevealed,
  pagination: controlledPagination,
  onPaginationChange,
  pageCount,
  totalRows,
  initialState,
  loading = false,
  loadingVariant = "skeleton",
  skeletonRowCount,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  emptyVariant = "table",
  showHeader = true,
  enableExpanding = false,
  renderSubComponent,
  getRowCanExpand,
  expanded: controlledExpanded,
  onExpandedChange,
  rowClassName,
  tree,
  dragDrop,
  layoutMode = "fixed",
  sortIndicatorVariant = "hover",
  fitContentHeight = false,
  frame: frameVariant = "card",
  horizontalScroll = false,
  "data-testid": dataTestId
}: KuikaTableProps<T>) => {
  const [revealedRowId, setRevealedRowId] = React.useState<string | null>(null);
  const frameRef: React.RefObject<HTMLDivElement> = React.useRef<HTMLDivElement>(null);
  const scrollerRef: React.RefObject<HTMLDivElement> = React.useRef<HTMLDivElement>(null);
  // `horizontalScroll`: whether the columns are wider than the frame right now (measured below).
  const [isColumnOverflowing, setIsColumnOverflowing] = React.useState<boolean>(false);
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);
  const [internalColumnFilters, setInternalColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [internalGlobalFilter, setInternalGlobalFilter] = React.useState<string>("");
  const [internalColumnVisibility, setInternalColumnVisibility] = React.useState<VisibilityState>({});
  const [internalRowSelection, setInternalRowSelection] = React.useState<RowSelectionState>({});
  const [internalColumnPinning, setInternalColumnPinning] = React.useState<ColumnPinningState>({});
  const [internalPagination, setInternalPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize });
  const [internalExpanded, setInternalExpanded] = React.useState<ExpandedState>({});
  // A drag is a whole-table mode, not a per-row one: while it runs every handle shows
  // (so you can see what else can move), the cursor stays grabbing anywhere you go,
  // and the groups that are not the target step back.
  const [isDragging, setIsDragging] = React.useState<boolean>(false);

  const sorting = controlledSorting ?? internalSorting;
  const columnFilters = controlledColumnFilters ?? internalColumnFilters;
  const globalFilter = controlledGlobalFilter ?? internalGlobalFilter;
  const columnVisibility = controlledColumnVisibility ?? internalColumnVisibility;
  const rowSelection = controlledRowSelection ?? internalRowSelection;
  const columnPinning = controlledColumnPinning ?? internalColumnPinning;
  const pagination = controlledPagination ?? internalPagination;
  const expanded = controlledExpanded ?? internalExpanded;

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    if (onSortingChange) {
      onSortingChange(updater);
    } else {
      const newState = typeof updater === "function" ? updater(sorting) : updater;
      setInternalSorting(newState);
    }
  };

  const handleColumnFiltersChange: OnChangeFn<ColumnFiltersState> = (updater) => {
    if (onColumnFiltersChange) {
      onColumnFiltersChange(updater);
    } else {
      const newState = typeof updater === "function" ? updater(columnFilters) : updater;
      setInternalColumnFilters(newState);
    }
  };

  const handleGlobalFilterChange: OnChangeFn<string> = (updater) => {
    if (onGlobalFilterChange) {
      onGlobalFilterChange(updater);
    } else {
      const newState = typeof updater === "function" ? updater(globalFilter) : updater;
      setInternalGlobalFilter(newState);
    }
  };

  const handleColumnVisibilityChange: OnChangeFn<VisibilityState> = (updater) => {
    if (onColumnVisibilityChange) {
      onColumnVisibilityChange(updater);
    } else {
      const newState = typeof updater === "function" ? updater(columnVisibility) : updater;
      setInternalColumnVisibility(newState);
    }
  };

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
    if (onRowSelectionChange) {
      onRowSelectionChange(updater);
    } else {
      const newState = typeof updater === "function" ? updater(rowSelection) : updater;
      setInternalRowSelection(newState);
    }
  };

  const handleColumnPinningChange: OnChangeFn<ColumnPinningState> = (updater) => {
    if (onColumnPinningChange) {
      onColumnPinningChange(updater);
    } else {
      const newState = typeof updater === "function" ? updater(columnPinning) : updater;
      setInternalColumnPinning(newState);
    }
  };

  const handlePaginationChange: OnChangeFn<PaginationState> = (updater) => {
    if (onPaginationChange) {
      onPaginationChange(updater);
    } else {
      const newState = typeof updater === "function" ? updater(pagination) : updater;
      setInternalPagination(newState);
    }
  };

  const handleExpandedChange: OnChangeFn<ExpandedState> = (updater) => {
    if (onExpandedChange) {
      onExpandedChange(updater);
    } else {
      const newState = typeof updater === "function" ? updater(expanded) : updater;
      setInternalExpanded(newState);
    }
  };

  // tanstack's flexRender treats a function `cell` as a component type, so a caller
  // that builds its columns inline gets a new type every render and React remounts
  // the cell — which destroys focus in an input mid-typing. Keep one stable renderer
  // component per column id and let it call the latest closure through a ref.
  const latestCellRenderers = React.useRef<Map<string, KuikaTableColumn<T>["cell"]>>(new Map());
  const stableCellComponents = React.useRef<Map<string, (context: CellContext<T, unknown>) => React.ReactNode>>(
    new Map()
  );
  columns.forEach((c) => latestCellRenderers.current.set(c.id, c.cell));
  const getStableCellComponent = (id: string): ((context: CellContext<T, unknown>) => React.ReactNode) => {
    const existing = stableCellComponents.current.get(id);
    if (existing) {
      return existing;
    }
    const component = (context: CellContext<T, unknown>): React.ReactNode => {
      const render = latestCellRenderers.current.get(id);
      return render ? render(context.row.original as T, context.row) : null;
    };
    stableCellComponents.current.set(id, component);
    return component;
  };

  const userColDefs: ColumnDef<T, any>[] = React.useMemo(
    () =>
      columns.map((c) => {
        const variant: KuikaTableFilterVariant = c.filterVariant ?? "text";
        // Multiselect filter stores an array of selected values. A row passes
        // when its cell value (stringified) is in the selection. Empty array
        // means "no selection" — show everything.
        const multiselectFilter = (row: Row<T>, columnId: string, filterValue: unknown) => {
          const arr = Array.isArray(filterValue) ? (filterValue as unknown[]) : [];
          if (arr.length === 0) return true;
          const cellValue = row.getValue(columnId);
          const asString = cellValue == null ? "" : String(cellValue);
          return arr.map((v) => String(v ?? "")).includes(asString);
        };
        return {
          id: c.id,
          header: c.header,
          cell: getStableCellComponent(c.id),
          size: typeof c.width === "number" ? c.width : undefined,
          accessorKey: (c.accessorKey as string | undefined) ? (c.accessorKey as string) : undefined,
          accessorFn: c.accessorFn ? (row: T) => c.accessorFn!(row) : undefined,
          enableSorting: c.enableSorting,
          sortDescFirst: c.sortDescFirst,
          enableColumnFilter: c.enableColumnFilter,
          enableResizing: c.enableResizing,
          filterFn: variant === "multiselect" ? multiselectFilter : "includesString",
          meta: {
            widthValue: typeof c.width === "string" ? c.width : undefined,
            minWidth: c.minWidth,
            align: c.align ?? "left",
            noWrap: c.noWrap ?? false,
            cellClassName: c.cellClassName,
            filterVariant: variant,
            filterValueLabel: c.filterValueLabel,
            headerTestId: c.headerTestId
          } as KuikaTableColumnMeta<T>
        } as ColumnDef<T, any>;
      }),
    [columns]
  );

  const selectionCol: ColumnDef<T, any> | null = enableRowSelection
    ? {
        id: "__select",
        header: ({ table }) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <KuikaCheckbox
              label={undefined}
              checked={table.getIsAllPageRowsSelected() || table.getIsSomePageRowsSelected()}
              onChange={() => table.toggleAllPageRowsSelected()}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <KuikaCheckbox label={undefined} checked={row.getIsSelected()} onChange={() => row.toggleSelected()} />
          </div>
        ),
        size: 40,
        enableSorting: false,
        enableColumnFilter: false,
        meta: { align: "center" }
      }
    : null;

  const colDefs: ColumnDef<T, any>[] = React.useMemo(() => {
    if (selectionCol) {
      return [selectionCol, ...userColDefs];
    }
    return userColDefs;
  }, [selectionCol, userColDefs]);

  /**
   * The table cuts the pages itself. Only then does a tree page by its top-level rows
   * (`paginateExpandedRows: false`): an open parent keeps its children on its own page, and the
   * pager counts what the footer counts. Anywhere else tanstack serves the rows straight from its
   * expanded model, which under that flag would not flatten the children at all.
   */
  const isClientPaginated: boolean = enablePagination && !manualPagination;

  const table = useReactTable<T>({
    data,
    columns: colDefs,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
      rowSelection,
      columnPinning,
      pagination,
      // A tree hands its open/closed policy to `tree.isRowExpanded`, but
      // getExpandedRowModel bails out early unless `expanded` is `true` or a
      // non-empty record — so nothing would ever expand. `true` clears that guard;
      // getIsRowExpanded is consulted first and still decides every row.
      expanded: tree ? true : expanded
    },
    onSortingChange: handleSortingChange,
    manualSorting,
    onColumnFiltersChange: handleColumnFiltersChange,
    onGlobalFilterChange: handleGlobalFilterChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onRowSelectionChange: handleRowSelectionChange,
    onColumnPinningChange: handleColumnPinningChange,
    onPaginationChange: handlePaginationChange,
    onExpandedChange: handleExpandedChange,
    getRowId: (row) => rowKey(row),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    getSortedRowModel: enableSorting && !manualSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: enableColumnFilters || enableGlobalFilter ? getFilteredRowModel() : undefined,
    getFacetedRowModel: enableColumnFilters ? getFacetedRowModel() : undefined,
    getFacetedUniqueValues: enableColumnFilters ? getFacetedUniqueValues() : undefined,
    getFacetedMinMaxValues: enableColumnFilters ? getFacetedMinMaxValues() : undefined,
    getExpandedRowModel: enableExpanding || tree ? getExpandedRowModel() : undefined,
    getRowCanExpand: getRowCanExpand ? (row) => getRowCanExpand(row.original) : undefined,
    getSubRows: tree ? (row: T, index: number) => tree.getSubRows(row, index) : undefined,
    getIsRowExpanded: tree ? (row: Row<T>) => tree.isRowExpanded(row.original) : undefined,
    // The caller owns expansion, so tanstack must not wipe it when the data changes —
    // a refetch would otherwise collapse every open node.
    autoResetExpanded: tree ? false : undefined,
    // Always a boolean: an explicit `undefined` would replace tanstack's `true` default, not fall back to it.
    paginateExpandedRows: !isClientPaginated,
    enableMultiSort,
    manualPagination: enablePagination ? manualPagination : false,
    autoResetPageIndex,
    pageCount,
    columnResizeMode: enableColumnResizing ? columnResizeMode : undefined,
    initialState: initialState ?? { pagination: { pageIndex: 0, pageSize } },
    enableSorting,
    enableFilters: enableColumnFilters,
    enableGlobalFilter,
    enableRowSelection,
    enableColumnPinning: enablePinning,
    enableColumnResizing
  });

  const total = totalRows ?? data.length;
  const hasRows: boolean = table.getRowModel().rows.length > 0;
  /**
   * Skeleton rows stand in for rows only while there are none on screen: a reload over rows keeps
   * them, dimmed under the spinner, because rows turning back into shimmer read as lost data. A
   * table with no visible columns has no shape to preview, so it keeps the spinner.
   */
  const canDrawSkeleton: boolean = loadingVariant === "skeleton" && table.getVisibleLeafColumns().length > 0;
  const loadingPresentation: LoadingPresentation = useLoadingPresentation({
    isLoading: loading && canDrawSkeleton,
    hasData: hasRows
  });
  // `pending`: the skeleton is not due yet — its rows are laid out but hidden, holding the space.
  const isSkeletonPending: boolean = loadingPresentation === "pending";
  const showSkeleton: boolean = isSkeletonPending || loadingPresentation === "skeleton";
  const isSpinning: boolean = loading && !showSkeleton;
  /**
   * The spinner only needs a reserved band when there is nothing underneath it. With
   * rows on screen it overlays them at their natural height — otherwise every
   * refresh would inflate a short table to 300px and snap back when it settles.
   */
  const reserveLoadingSpace: boolean = isSpinning && !hasRows;
  const skeletonRows: number = skeletonRowCount ?? Math.min(pageSize, FIRST_LOAD_SKELETON_ROWS);
  // pageIndex is 0-indexed
  const currentPageIndex = table.getState().pagination.pageIndex;
  const currentPageSize = table.getState().pagination.pageSize;
  const totalPageCount = table.getPageCount();
  const from = currentPageIndex * currentPageSize + 1;
  const to = Math.min(from + currentPageSize - 1, total);
  const pageIndexes = React.useMemo(() => {
    if (totalPageCount <= 7) {
      return Array.from({ length: totalPageCount }, (_, idx) => idx);
    }
    const start = Math.max(0, Math.min(currentPageIndex - 3, totalPageCount - 7));
    return Array.from({ length: 7 }, (_, idx) => start + idx);
  }, [currentPageIndex, totalPageCount]);

  // A reveal stays armed until the row is among the rows shown, and only then is reported as
  // done: the caller asks for it as soon as it has saved, which can be a render or two before
  // the refreshed rows arrive — acting once would act on the old order. A paged table first
  // turns to the row's page (a hard cut); either way the row says so on arrival: it lights up
  // and fades back, which is what the reader's eye follows.
  React.useEffect((): void => {
    if (!revealRowKey) return;
    if (enablePagination) {
      // Pre-pagination rows: a row's place in the whole list is what names its page. When the
      // table pages by top-level rows, that list holds only those, so a child is placed by its root.
      const prePaginationRows: RowModel<T> = table.getPrePaginationRowModel();
      const revealRow: Row<T> | undefined = prePaginationRows.rowsById[revealRowKey];
      if (!revealRow) return;
      const pagedRowId: string = isClientPaginated ? (revealRow.getParentRows()[0]?.id ?? revealRow.id) : revealRow.id;
      const index: number = prePaginationRows.rows.findIndex((row: Row<T>): boolean => row.id === pagedRowId);
      if (index < 0) return;
      const target: number = Math.floor(index / currentPageSize);
      if (target !== currentPageIndex) {
        table.setPageIndex(target);
        return;
      }
    }
    // On its page, or with no pages at all, the row still waits for a closed parent to open.
    if (!table.getRowModel().rows.some((row: Row<T>): boolean => row.id === revealRowKey)) return;
    setRevealedRowId(revealRowKey);
    onRowRevealed?.();
  }, [
    revealRowKey,
    enablePagination,
    isClientPaginated,
    currentPageIndex,
    currentPageSize,
    table,
    onRowRevealed,
    data
  ]);

  // Bring the row into view (the page or panel around the table scrolls it vertically: the frame
  // itself scrolls sideways at most), then drop the class once the fade has run; holding it longer would
  // repaint the row on every later render for nothing.
  React.useEffect((): (() => void) | undefined => {
    if (!revealedRowId) return undefined;
    frameRef.current?.querySelector<HTMLElement>(".kuika-table__row--revealed")?.scrollIntoView({ block: "nearest" });
    const timer: number = window.setTimeout((): void => setRevealedRowId(null), REVEAL_HIGHLIGHT_MS);
    return (): void => window.clearTimeout(timer);
  }, [revealedRowId]);

  // Clamp when the data shrinks so we never land on a blank page past the last one.
  React.useEffect(() => {
    if (autoResetPageIndex !== false) return;
    if (totalPageCount > 0 && currentPageIndex > totalPageCount - 1) {
      table.setPageIndex(totalPageCount - 1);
    }
  }, [autoResetPageIndex, currentPageIndex, totalPageCount, table]);

  const visibilityMenuOptions = React.useMemo(() => {
    if (!enableColumnVisibility) return [] as any[];
    return table.getAllLeafColumns().map((c) => ({
      key: c.id,
      label: (
        <div style={{ display: "flex", alignItems: "center" }}>
          <KuikaCheckbox
            label={
              typeof c.columnDef.header === "function" ? undefined : (c.columnDef.header as unknown as string) || c.id
            }
            checked={c.getIsVisible()}
            onChange={() => c.toggleVisibility()}
          />
          {typeof c.columnDef.header === "function" && (
            <KuikaSmallBoldText style={{ marginLeft: 8 }}>{c.id}</KuikaSmallBoldText>
          )}
        </div>
      ),
      showSelectedIcon: false
    }));
  }, [enableColumnVisibility, table, columnVisibility]);

  const handleDragStart = (): void => setIsDragging(true);

  const handleDragEnd = (result: DropResult): void => {
    setIsDragging(false);
    dragDrop?.onDragEnd(result);
  };

  // One row, with or without RBD wiring. Extracted so the body can render flat OR
  // split into one <tbody> per drag group without duplicating the cell markup.
  const renderRow = (r: Row<T>, dragIndex: number): React.ReactNode => {
    const original: T = r.original as T;
    const canDrag: boolean = !!dragDrop && (dragDrop.isRowDraggable?.(original) ?? true);

    const cells = r.getVisibleCells().map((c) => {
      const meta = c.column.columnDef.meta as KuikaTableColumnMeta<T> | undefined;
      const align = meta?.align ?? "left";
      const noWrap = meta?.noWrap ?? false;
      const cellClassName = meta?.cellClassName ? meta.cellClassName(original) : "";
      return (
        <td
          key={c.id}
          className={classNames(`kuika-table__align--${align}`, cellClassName, {
            "kuika-table__cell--tree": tree?.treeColumnId === c.column.id
          })}
        >
          {noWrap ? (
            flexRender(c.column.columnDef.cell, c.getContext())
          ) : (
            // Plain span, not KuikaText: that stamps `title={children}`, and a cell's
            // children are elements, so every cell got a native "[object Object]" tooltip.
            <span className="kuika-typography-text kuika-table__cell-text">
              {flexRender(c.column.columnDef.cell, c.getContext())}
            </span>
          )}
        </td>
      );
    });

    const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
      // Don't trigger row click if clicking on interactive elements
      const target = e.target as HTMLElement;
      if (
        target.closest("button") ||
        target.closest("a") ||
        target.closest(".kuika-dropdown__menu") ||
        target.closest("[role='button']")
      ) {
        return;
      }
      onRowClick?.(original);
    };

    // `getIsExpanded` answers two different questions: "show my children" for a parent
    // and "show my detail panel" for a leaf. A row with children means the first, so
    // the detail panel is a leaf-only affair, and the two get their own classes rather
    // than one that means whichever the caller happens to be doing.
    const isExpanded: boolean = r.getIsExpanded();
    const isParentOpen: boolean = isExpanded && r.subRows.length > 0;
    // Resolved before the row is drawn, because "has a detail panel" is not the same
    // as "was given a renderSubComponent": a childless parent (an empty folder) is a
    // leaf by this test, and its renderSubComponent returns null. Rendering the <tr>
    // on the callback's mere existence left an empty padded band under every one.
    const detail: React.ReactNode =
      isExpanded && !isParentOpen && renderSubComponent ? renderSubComponent(original) : null;

    const isRevealed: boolean = r.id === revealedRowId;
    const rowClass = (extra?: string): string =>
      classNames(
        // The block every `kuika-table__row--*` below is a modifier of. It was never
        // emitted, so a caller styling `.kuika-table__row.<their-class>` — the obvious
        // way to reach a row of this table — silently matched nothing.
        "kuika-table__row",
        {
          "kuika-table__row--clickable": !!onRowClick,
          "kuika-table__row--tree-open": isParentOpen,
          "kuika-table__row--expanded": !!detail,
          "kuika-table__row--revealed": isRevealed
        },
        rowClassName?.(original, r),
        extra
      );

    // Depth drives the tree cell's indent from CSS, so nothing has to be measured or
    // recomputed per row. Only set on a tree (and on the one revealed row), to keep every
    // other table's markup as-is.
    const rowStyle: IKuikaTableRowStyle | undefined =
      tree || isRevealed
        ? {
            "--kuika-tree-depth": tree ? r.depth : undefined,
            "--kuika-table-reveal-duration": isRevealed ? `${REVEAL_HIGHLIGHT_MS}ms` : undefined
          }
        : undefined;

    const subRow = detail ? (
      <tr className="kuika-table__expanded-row">
        <td colSpan={r.getVisibleCells().length + (dragDrop ? 1 : 0)}>{detail}</td>
      </tr>
    ) : null;

    if (!dragDrop) {
      return (
        <React.Fragment key={rowKey(original)}>
          <tr
            onClick={handleRowClick}
            className={rowClass()}
            data-testid={rowTestId?.(original)}
            data-revealed={isRevealed ? "true" : undefined}
            style={onRowClick ? { ...rowStyle, cursor: "pointer" } : rowStyle}
          >
            {cells}
          </tr>
          {subRow}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={rowKey(original)}>
        <Draggable draggableId={dragDrop.getDraggableId(original)} index={dragIndex} isDragDisabled={!canDrag}>
          {(dragProvided, dragSnapshot) => (
            <tr
              ref={dragProvided.innerRef}
              {...dragProvided.draggableProps}
              onClick={handleRowClick}
              data-testid={rowTestId?.(original)}
              data-revealed={isRevealed ? "true" : undefined}
              className={rowClass(
                classNames({
                  "kuika-table__row--draggable": canDrag,
                  "kuika-table__row--dragging": dragSnapshot.isDragging
                })
              )}
              style={{
                ...dragProvided.draggableProps.style,
                ...rowStyle,
                cursor: onRowClick ? "pointer" : undefined
              }}
            >
              {/* The handle is its own leading cell so the grip has a fixed slot and
                  the row's own click still selects rather than starting a drag. The
                  drag props sit on the SPAN, not the cell: rbd makes whatever carries
                  them focusable and keyboard-draggable, and a focusable <td> is neither
                  reachable in a sensible tab order nor announceable. */}
              <td className="kuika-table__drag-cell">
                {canDrag && (
                  <span
                    className="kuika-table__drag-handle"
                    {...dragProvided.dragHandleProps}
                    aria-label={dragDrop.handleLabel ?? "Drag to move"}
                  >
                    <KuikaSvg name="grip-vertical" width={16} height={16} />
                  </span>
                )}
              </td>
              {cells}
            </tr>
          )}
        </Draggable>
        {subRow}
      </React.Fragment>
    );
  };

  const renderEmpty = (): React.ReactNode => {
    if (loading || data.length !== 0) return null;
    // The host draws its own empty state under the heading; the table adds no band.
    if (emptyVariant === "none") return null;
    return (
      <tr>
        <td colSpan={table.getAllColumns().length + (dragDrop ? 1 : 0)} className="kuika-table__empty-state">
          <KuikaEmptyState
            variant={emptyVariant}
            icon={emptyIcon}
            customName={emptyTitle}
            description={emptyDescription}
            // hideDescription drops the title WITH the body, and the title is what every caller
            // passes — so it keys on the title alone. Without one there is nothing to say and the
            // generated "No undefined added yet." must not appear.
            hideDescription={!emptyTitle}
          />
        </td>
      </tr>
    );
  };

  /**
   * Without drag-and-drop the rows are one plain run. With it, each consecutive run
   * of rows sharing a group id becomes its own `<tbody>` Droppable — a table may
   * legally hold several — so every group is a real drop target of its own.
   */
  const renderBody = (): React.ReactNode => {
    const rows: Row<T>[] = table.getRowModel().rows;
    if (rows.length === 0) return <tbody>{renderEmpty()}</tbody>;
    if (!dragDrop) return <tbody>{rows.map((r: Row<T>) => renderRow(r, 0))}</tbody>;

    const groups: { id: string; rows: Row<T>[] }[] = [];
    rows.forEach((r: Row<T>) => {
      const id: string = dragDrop.getGroupId(r.original as T);
      const last = groups[groups.length - 1];
      if (last && last.id === id) last.rows.push(r);
      else groups.push({ id, rows: [r] });
    });

    // Rows sharing a group MUST be contiguous. When they are not, the same id opens
    // two tbodies: React sees duplicate keys and RBD sees duplicate droppables, and
    // the group renders twice while updates stop applying to it. That is impossible
    // to spot from the symptom, so say it out loud instead of drawing it wrong.
    const seen: Set<string> = new Set();
    groups.forEach((group: { id: string; rows: Row<T>[] }) => {
      if (seen.has(group.id)) {
        // eslint-disable-next-line no-console
        console.error(
          `KuikaTable: rows for group "${group.id}" are not contiguous — order the data so every group's rows sit together.`
        );
      }
      seen.add(group.id);
    });

    // Pointer-aimed: a pointer drag lands in the group under the pointer, which rbd's own overlap
    // rule cannot reach when it is a one-row group below a neighbour (`kuika-pointer-dnd`).
    return groups.map((group: { id: string; rows: Row<T>[] }) => (
      <PointerAimedDroppable droppableId={group.id} key={group.id}>
        {(dropProvided, dropSnapshot) => (
          <tbody
            ref={dropProvided.innerRef}
            {...dropProvided.droppableProps}
            className={classNames("kuika-table__group", {
              // Hovering the group the row came FROM is not a move, so it must not
              // promise one. `draggingFromThisWith` is set on exactly that group,
              // which is the only way a droppable can tell "would land here" from
              // "is already here".
              "kuika-table__group--drag-over": dropSnapshot.isDraggingOver && !dropSnapshot.draggingFromThisWith,
              "kuika-table__group--drag-source": !!dropSnapshot.draggingFromThisWith
            })}
          >
            {group.rows.map((r: Row<T>, index: number) => renderRow(r, index))}
            {dropProvided.placeholder}
          </tbody>
        )}
      </PointerAimedDroppable>
    ));
  };

  const visibleLeafColumns: Column<T, unknown>[] = table.getVisibleLeafColumns();

  /** What a column's `<col>` declares: a pixel size, a CSS length, or nothing (it sizes itself). */
  const getColumnWidth = (col: Column<T, unknown>): number | string | undefined => {
    const declared: string | undefined = (col.columnDef.meta as KuikaTableColumnMeta<T> | undefined)?.widthValue;
    // A dragged column has a pixel size of its own and keeps it; otherwise a
    // declared CSS length goes straight onto the <col>, which is the one place
    // a share of the table can be expressed.
    if (declared !== undefined && !enableColumnResizing) {
      return declared;
    }
    if (enableColumnResizing || col.columnDef.size !== undefined) {
      return col.getSize();
    }
    return undefined;
  };

  /**
   * `horizontalScroll`'s floor: every pixel width, and the unsized minimum for each other column.
   * None for an auto layout, which the browser already sizes from its content, and none under the
   * empty state — it has no cells to keep readable, and its message must centre in the visible
   * frame, not in a table wider than it.
   */
  const getTableMinWidth = (): number | undefined => {
    const isShowingEmptyState: boolean = !showSkeleton && !loading && data.length === 0;
    if (!horizontalScroll || layoutMode !== "fixed" || isShowingEmptyState) return undefined;
    const dragColumnWidth: number = dragDrop ? DRAG_COLUMN_WIDTH : 0;
    return visibleLeafColumns.reduce((total: number, col: Column<T, unknown>): number => {
      const width: number | string | undefined = getColumnWidth(col);
      if (typeof width === "number") return total + width;
      const ownMinimum: number | undefined = (col.columnDef.meta as KuikaTableColumnMeta<T> | undefined)?.minWidth;
      return total + (ownMinimum ?? MIN_UNSIZED_COLUMN_WIDTH);
    }, dragColumnWidth);
  };
  const tableMinWidth: number | undefined = getTableMinWidth();

  // The scroller turns into a scroll container only while the columns overflow it. A scroll
  // container is what a sticky heading row pins to and what react-beautiful-dnd takes for a
  // droppable's one scroll parent, so in the usual wide frame the page keeps both roles. Measured
  // before paint on mount, then whenever the scroller or the table changes size.
  React.useLayoutEffect((): (() => void) | undefined => {
    const scroller: HTMLDivElement | null = scrollerRef.current;
    if (!horizontalScroll || !scroller) {
      setIsColumnOverflowing(false);
      return undefined;
    }
    // With or without the scroll class: a box that lets its overflow show still reports it as
    // scrollWidth. State only changes when the answer does, so a resize that keeps it is free.
    const measure = (): void => setIsColumnOverflowing(scroller.scrollWidth > scroller.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    // On the next frame, not inside the observer's callback: turning the scrollbar on changes the
    // scroller's height, and a size change made during the observer's own delivery is reported as
    // a "ResizeObserver loop" error.
    let frameId: number = 0;
    const observer: ResizeObserver = new ResizeObserver((): void => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(measure);
    });
    observer.observe(scroller);
    const tableElement: Element | null = scroller.firstElementChild;
    if (tableElement) observer.observe(tableElement);
    return (): void => {
      observer.disconnect();
      window.cancelAnimationFrame(frameId);
    };
  }, [horizontalScroll, tableMinWidth]);

  const frame = (
    <div
      ref={frameRef}
      className={classNames("kuika-table", {
        "kuika-table--flush": frameVariant === "flush",
        "kuika-table--sort-inline": sortIndicatorVariant === "inline",
        "kuika-table--dragging": isDragging
      })}
      data-testid={dataTestId}
    >
      {(enableGlobalFilter || enableColumnVisibility) && (
        <div className="kuika-table__toolbar">
          {enableGlobalFilter ? (
            <div className="kuika-table__global-filter">
              <KuikaInput
                placeholder="Search"
                leftIcon={<KuikaSvg name="search" width={14} height={14} color="var(--dark-5)" />}
                value={globalFilter ?? ""}
                onChange={(_, v) => handleGlobalFilterChange(String(v))}
                size="md"
              />
            </div>
          ) : (
            <div />
          )}
          {enableColumnVisibility && (
            <div className="kuika-table__visibility">
              <KuikaDropdownMenu
                options={visibilityMenuOptions}
                closeOnSelect={false}
                overlayClassName="kuika-table__visibility-menu"
              >
                <KuikaButton type="tertiary" size="sm">
                  <KuikaSvg name="components" width={16} height={16} style={{ marginRight: 8 }} />
                  Columns
                </KuikaButton>
              </KuikaDropdownMenu>
            </div>
          )}
        </div>
      )}
      <KuikaSpinnerSmall
        spinning={isSpinning}
        size="large"
        className={classNames("kuika-table__loading", {
          "kuika-table__loading--active": isSpinning,
          "kuika-table__loading--reserve": reserveLoadingSpace
        })}
      >
        <div
          className={classNames("kuika-table__container", {
            "kuika-table__container--auto-layout": layoutMode === "auto",
            "kuika-table__container--fit-content": fitContentHeight
          })}
          aria-busy={loading || undefined}
        >
          {loading && (
            <span className="kuika-visually-hidden" role="status">
              Loading
            </span>
          )}
          {/* The one thing that scrolls sideways (`horizontalScroll`). It wraps the table alone: the
              container's filler below it has to stay put, so the rule it draws under the last row
              spans the visible frame however far the columns are scrolled. */}
          <div
            ref={scrollerRef}
            className={classNames("kuika-table__scroller", {
              "kuika-table__scroller--scroll": horizontalScroll && isColumnOverflowing
            })}
          >
            <table
              className={classNames("kuika-table__element", {
                "kuika-table__element--auto-layout": layoutMode === "auto"
              })}
              style={tableMinWidth !== undefined ? { minWidth: tableMinWidth } : undefined}
            >
              <colgroup>
                {/* The drag handle is an injected cell with no tanstack column behind it,
                    so it needs an injected <col> to match. Without one the colgroup is a
                    column short and the browser pairs entries to cells positionally: every
                    width lands on its LEFT neighbour, the last real column is squeezed
                    into the last declared width, and the actions column — left with no
                    entry at all — swallows whatever is left. */}
                {dragDrop && <col className="kuika-table__drag-col" />}
                {visibleLeafColumns.map((col: Column<T, unknown>): JSX.Element => (
                  <col key={col.id} style={{ width: getColumnWidth(col) }} />
                ))}
              </colgroup>
              {showHeader && (
                <thead>
                  {table.getHeaderGroups().map((hg) => (
                    <tr key={hg.id}>
                      {/* Keeps the header aligned with each row's leading drag cell. */}
                      {dragDrop && <th className="kuika-table__drag-cell" aria-hidden="true" />}
                      {hg.headers.map((h) => (
                        <th
                          key={h.id}
                          className={`kuika-table__align--${(h.column.columnDef.meta as any)?.align ?? "left"}`}
                          // Same test as the colgroup above: a column that declares no width must
                          // stay unset here too. `getSize()` answers tanstack's default 150 for
                          // one, and stamping that on the header contradicted the colgroup — the
                          // declared widths no longer added up to the table, so the browser shared
                          // the surplus across every column and none of them kept its width.
                          style={{
                            width:
                              layoutMode === "auto"
                                ? "auto"
                                : enableColumnResizing || h.column.columnDef.size !== undefined
                                  ? h.getSize()
                                  : undefined
                          }}
                          aria-sort={
                            h.column.getIsSorted()
                              ? h.column.getIsSorted() === "asc"
                                ? "ascending"
                                : "descending"
                              : "none"
                          }
                          data-testid={(h.column.columnDef.meta as KuikaTableColumnMeta<T> | undefined)?.headerTestId}
                        >
                          <div
                            className="kuika-table__header-content"
                            onClick={() => (h.column.getCanSort() ? h.column.toggleSorting() : undefined)}
                            role={h.column.getCanSort() ? "button" : undefined}
                            tabIndex={h.column.getCanSort() ? 0 : -1}
                            onKeyDown={(e) => {
                              if (h.column.getCanSort() && (e.key === "Enter" || e.key === " ")) {
                                e.preventDefault();
                                h.column.toggleSorting();
                              }
                            }}
                          >
                            <KuikaSmallBoldText className="kuika-table__header-text">
                              {flexRender(h.column.columnDef.header, h.getContext())}
                            </KuikaSmallBoldText>
                            {h.column.getCanSort() && (
                              <span
                                className={`kuika-table__sort-indicator${
                                  h.column.getIsSorted() ? " kuika-table__sort-indicator--active" : ""
                                }`}
                              >
                                {/* "inline" keeps a static arrows-move-vertical in every sort state,
                                    matching the pre-migration header; only the default variant flips
                                    to a directional chevron when actively sorted. */}
                                {sortIndicatorVariant === "inline" && (
                                  <KuikaSvg
                                    name="arrows-move-vertical"
                                    type="arrows"
                                    width={12}
                                    height={12}
                                    color="var(--dark-7)"
                                  />
                                )}
                                {sortIndicatorVariant !== "inline" && h.column.getIsSorted() === "asc" && (
                                  <KuikaSvg
                                    name="chevron-up"
                                    type="arrows"
                                    width={12}
                                    height={12}
                                    color="var(--blue-5)"
                                  />
                                )}
                                {sortIndicatorVariant !== "inline" && h.column.getIsSorted() === "desc" && (
                                  <KuikaSvg
                                    name="chevron-down"
                                    type="arrows"
                                    width={12}
                                    height={12}
                                    color="var(--blue-5)"
                                  />
                                )}
                                {sortIndicatorVariant !== "inline" && !h.column.getIsSorted() && (
                                  <span className="kuika-table__sort-neutral">
                                    <KuikaSvg
                                      name="chevron-up"
                                      type="arrows"
                                      width={10}
                                      height={10}
                                      color="var(--dark-6)"
                                    />
                                    <KuikaSvg
                                      name="chevron-down"
                                      type="arrows"
                                      width={10}
                                      height={10}
                                      color="var(--dark-6)"
                                    />
                                  </span>
                                )}
                              </span>
                            )}
                            {enableColumnFilters && h.column.getCanFilter() && <ColumnFilterButton column={h.column} />}
                          </div>
                          {enableColumnResizing && h.column.getCanResize() && (
                            <div
                              onMouseDown={h.getResizeHandler()}
                              onTouchStart={h.getResizeHandler()}
                              className="kuika-table__resizer"
                            />
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
              )}
              {showSkeleton && (
                <tbody
                  className={classNames("kuika-table__skeleton", {
                    "kuika-table__skeleton--pending": isSkeletonPending
                  })}
                  aria-hidden="true"
                >
                  {Array.from({ length: skeletonRows }, (_unused: unknown, rowIdx: number) => (
                    <tr key={`kuika-skeleton-${rowIdx}`} className="kuika-table__skeleton-row">
                      {/* The drag handle's injected cell, as in the header and the empty state. */}
                      {dragDrop && <td className="kuika-table__skeleton-handle" />}
                      {table.getVisibleLeafColumns().map((col, colIdx: number) => (
                        <td key={col.id}>
                          <div
                            className={classNames("kuika-table__skeleton-cell", {
                              "kuika-table__skeleton-cell--lead": colIdx === 0
                            })}
                          >
                            {colIdx === 0 && (
                              <KuikaSkeleton
                                className="kuika-table__skeleton-square"
                                width={SKELETON_SQUARE_SIZE}
                                height={SKELETON_SQUARE_SIZE}
                              />
                            )}
                            <KuikaSkeleton
                              className="kuika-table__skeleton-bar"
                              width={colIdx === 0 ? "100%" : "55%"}
                              height={SKELETON_BAR_HEIGHT}
                            />
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              )}
              {!showSkeleton && renderBody()}
            </table>
          </div>
        </div>
      </KuikaSpinnerSmall>
      {/* Nothing to page or count yet while the skeleton stands in for the rows. */}
      {!showSkeleton && (enablePagination || showCountLabel) && (
        <div className="kuika-table__footer">
          <div className="kuika-table__footer-left">
            {showCountLabel && (
              <KuikaTextMedium className="kuika-table__count">
                {showCountLabel} ({total})
              </KuikaTextMedium>
            )}
          </div>
          {enablePagination && (
            <div className="kuika-table__footer-right">
              <KuikaButton
                type="tertiary"
                size="sm"
                isIcon
                active={false}
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <KuikaSvg name="chevron-left" type="arrows" width={16} height={16} />
              </KuikaButton>
              {pageIndexes[0] > 0 && <span className="kuika-table__page-ellipsis">…</span>}
              {pageIndexes.map((idx) => (
                <KuikaButton
                  key={`p-${idx}`}
                  type="tertiary"
                  size="sm"
                  active={table.getState().pagination.pageIndex === idx}
                  onClick={() => table.setPageIndex(idx)}
                >
                  {idx + 1}
                </KuikaButton>
              ))}
              {pageIndexes[pageIndexes.length - 1] < table.getPageCount() - 1 && (
                <span className="kuika-table__page-ellipsis">…</span>
              )}
              <KuikaButton
                type="tertiary"
                size="sm"
                isIcon
                active={false}
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <KuikaSvg name="chevron-right" type="arrows" width={16} height={16} />
              </KuikaButton>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // The context has to sit OUTSIDE the frame: its Droppables are the row groups
  // inside the table, and RBD requires a single provider above all of them.
  if (!dragDrop) return frame;
  return (
    <PointerAimedDragDropContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragUpdate={dragDrop.onDragUpdate}
    >
      {frame}
    </PointerAimedDragDropContext>
  );
};

// ══════════════════════════════════════════════════════════════════════════
//  Per-column filter button — header popover with text or multiselect UI
// ══════════════════════════════════════════════════════════════════════════

interface ColumnFilterButtonProps<T> {
  column: Column<T, unknown>;
}

// Turn a camelCase / snake_case / kebab-case column id into a readable
// "Created At"-style title, used only when the header isn't a plain string.
function humanizeColumnId(id: string): string {
  const spaced: string = id.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ");
  return spaced
    .split(" ")
    .filter(Boolean)
    .map((word: string): string => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function ColumnFilterButton<T>({ column }: ColumnFilterButtonProps<T>) {
  const meta = column.columnDef.meta as
    | {
        filterVariant?: KuikaTableFilterVariant;
        filterValueLabel?: (value: unknown) => string;
      }
    | undefined;
  const variant: KuikaTableFilterVariant = meta?.filterVariant ?? "text";
  const labelFn = meta?.filterValueLabel ?? ((v: unknown) => (v == null ? "—" : String(v)));
  const filterValue = column.getFilterValue();
  const isActive =
    variant === "multiselect"
      ? Array.isArray(filterValue) && (filterValue as unknown[]).length > 0
      : typeof filterValue === "string" && filterValue.length > 0;

  // Column header may be a render fn returning a ReactNode; for the popover
  // title we prefer a plain string. Fall back to the column id.
  const headerDef = column.columnDef.header;
  const headerTitle = typeof headerDef === "string" ? headerDef : humanizeColumnId(column.id);
  const activeCount = variant === "multiselect" && Array.isArray(filterValue) ? (filterValue as unknown[]).length : 0;

  return (
    <KuikaPopover
      position="bottom"
      hideCloseIcon
      content={
        <div
          className="kuika-table__filter-popover"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <div className="kuika-table__filter-header">
            <span className="title">Filter {headerTitle}</span>
            {isActive && (
              <KuikaButton type="text" size="sm" onClick={() => column.setFilterValue(undefined)}>
                Reset
              </KuikaButton>
            )}
          </div>
          {variant === "multiselect" ? (
            <MultiselectFilterBody column={column} labelFn={labelFn} />
          ) : (
            <TextFilterBody column={column} />
          )}
        </div>
      }
    >
      <button
        type="button"
        className={classNames("kuika-table__filter-btn", {
          "kuika-table__filter-btn--active": isActive
        })}
        aria-label={`Filter ${headerTitle}`}
        title={
          activeCount > 0 ? `${activeCount} filter${activeCount === 1 ? "" : "s"} applied` : `Filter ${headerTitle}`
        }
        onClick={(e) => {
          // Prevent the parent header click-to-sort from triggering.
          e.stopPropagation();
        }}
      >
        <KuikaSvg name="filter" width={13} height={13} color={isActive ? "var(--blue-5)" : "var(--dark-7)"} />
      </button>
    </KuikaPopover>
  );
}

function TextFilterBody<T>({ column }: { column: Column<T, unknown> }) {
  const value = (column.getFilterValue() as string) ?? "";
  return (
    <div className="kuika-table__filter-text">
      <KuikaInput
        placeholder="Contains…"
        size="md"
        value={value}
        onChange={(_, v) => column.setFilterValue(String(v ?? ""))}
        leftIcon={<KuikaSvg name="search" width={14} height={14} color="var(--dark-5)" />}
      />
    </div>
  );
}

function MultiselectFilterBody<T>({
  column,
  labelFn
}: {
  column: Column<T, unknown>;
  labelFn: (value: unknown) => string;
}) {
  const [search, setSearch] = React.useState("");
  const selected = React.useMemo<string[]>(() => {
    const raw = column.getFilterValue();
    return Array.isArray(raw) ? (raw as unknown[]).map((v) => String(v ?? "")) : [];
  }, [column.getFilterValue()]);

  // Faceted unique values — populated by tanstack's getFacetedUniqueValues
  // when enableColumnFilters is on. Map<value, occurrenceCount>. We also
  // include anything currently selected even if it isn't in the facet (so
  // stale selections don't silently disappear).
  const facet = column.getFacetedUniqueValues();
  const values = React.useMemo(() => {
    const seen = new Set<string>();
    const out: { raw: unknown; key: string; count: number }[] = [];
    facet?.forEach((count, raw) => {
      const key = String(raw ?? "");
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ raw, key, count });
    });
    for (const key of selected) {
      if (!seen.has(key)) {
        seen.add(key);
        out.push({ raw: key, key, count: 0 });
      }
    }
    out.sort((a, b) => labelFn(a.raw).localeCompare(labelFn(b.raw), undefined, { sensitivity: "base" }));
    const needle = search.trim().toLowerCase();
    return needle ? out.filter((v) => labelFn(v.raw).toLowerCase().includes(needle)) : out;
  }, [facet, selected, search, labelFn]);

  const toggle = (key: string) => {
    const next = selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key];
    column.setFilterValue(next.length === 0 ? undefined : next);
  };

  return (
    <div className="kuika-table__filter-multiselect">
      <KuikaInput
        placeholder="Search values…"
        size="md"
        value={search}
        onChange={(_, v) => setSearch(String(v ?? ""))}
        leftIcon={<KuikaSvg name="search" width={14} height={14} color="var(--dark-5)" />}
      />
      <div className="kuika-table__filter-list">
        {values.length === 0 ? (
          <div className="kuika-table__filter-empty">No matching values</div>
        ) : (
          values.map((v) => (
            <label key={v.key} className="kuika-table__filter-option" onClick={(e) => e.stopPropagation()}>
              <KuikaCheckbox checked={selected.includes(v.key)} onChange={() => toggle(v.key)} />
              <span className="label">{labelFn(v.raw)}</span>
              {v.count > 0 && <span className="count">{v.count}</span>}
            </label>
          ))
        )}
      </div>
      <div className="kuika-table__filter-actions">
        <span className="kuika-table__filter-summary">
          {selected.length > 0
            ? `${selected.length} selected`
            : `${values.length} value${values.length === 1 ? "" : "s"}`}
        </span>
      </div>
    </div>
  );
}

export default KuikaTable;
